import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, ChevronLeft, UserPlus, X, LayoutDashboard, FileText } from "lucide-react";
import { adminApi } from "../../api";
import { userReportsApi } from "../../api/userReports";
import client from "../../api/client";
import { Button, LoadingText, Select } from "../ui";
import type { AppUser, Team as BaseTeam } from "../../types/user";

interface Team extends BaseTeam {
  description: string | null;
  created_at: string;
  member_count: number;
  dashboard_count: number;
}

interface TeamDashboard {
  id: number;
  name: string;
}

interface TeamReport {
  id: string;
  name: string;
}

interface TeamDetail {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  members: AppUser[];
  default_dashboards: TeamDashboard[];
  default_reports: TeamReport[];
}

interface AvailableDashboard {
  id: number;
  name: string;
}

interface AvailableReport {
  id: string;
  name: string;
}

interface TeamsSettingsProps {
  users: AppUser[];
}

export default function TeamsSettings({ users }: TeamsSettingsProps) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState<TeamDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  // Edit team name/desc
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editError, setEditError] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Add member
  const [addMemberUserId, setAddMemberUserId] = useState("");
  const [addingMember, setAddingMember] = useState(false);

  // Add scheduled dashboard
  const [availableDashboards, setAvailableDashboards] = useState<AvailableDashboard[]>([]);
  const [addDashboardId, setAddDashboardId] = useState("");
  const [addingDashboard, setAddingDashboard] = useState(false);

  // Add default report
  const [availableReports, setAvailableReports] = useState<AvailableReport[]>([]);
  const [addReportId, setAddReportId] = useState("");
  const [addingReport, setAddingReport] = useState(false);

  async function loadTeams() {
    setLoading(true);
    try {
      const res = await adminApi.listTeams();
      setTeams(res.data);
    } catch { return; } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadTeams(); }, []);

  useEffect(() => {
    if (!selectedTeam) return;
    client.get("/dashboards")
      .then((res: { data: AvailableDashboard[] }) => setAvailableDashboards(res.data || []))
      .catch(() => {});
    userReportsApi.discover({ page_size: 200 })
      .then((res) => setAvailableReports(res.data?.items || []))
      .catch(() => {});
  }, [selectedTeam]);

  const openTeam = useCallback(async (teamId: string) => {
    setDetailLoading(true);
    try {
      const res = await adminApi.getTeam(teamId);
      setSelectedTeam(res.data);
      setEditName(res.data.name);
      setEditDesc(res.data.description ?? "");
      setEditing(false);
    } catch { return; } finally {
      setDetailLoading(false);
    }
  }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError("");
    const name = createName.trim();
    if (!name) { setCreateError("Name is required."); return; }
    setCreating(true);
    try {
      await adminApi.createTeam({ name, description: createDesc.trim() || undefined });
      setCreateName(""); setCreateDesc(""); setShowCreate(false);
      await loadTeams();
    } catch { return; } finally {
      setCreating(false);
    }
  }

  async function handleDelete(team: Team) {
    if (!confirm(`Delete team "${team.name}"? Members will be unassigned.`)) return;
    await adminApi.deleteTeam(team.id);
    if (selectedTeam?.id === team.id) setSelectedTeam(null);
    await loadTeams();
  }

  async function handleSaveEdit() {
    if (!selectedTeam) return;
    setEditError("");
    const name = editName.trim();
    if (!name) { setEditError("Name is required."); return; }
    setEditSaving(true);
    try {
      await adminApi.updateTeam(selectedTeam.id, { name, description: editDesc.trim() || undefined });
      setEditing(false);
      await openTeam(selectedTeam.id);
      await loadTeams();
    } catch { return; } finally {
      setEditSaving(false);
    }
  }

  async function handleAddMember() {
    if (!selectedTeam || !addMemberUserId) return;
    setAddingMember(true);
    try {
      await adminApi.addTeamMember(selectedTeam.id, addMemberUserId);
      setAddMemberUserId("");
      await openTeam(selectedTeam.id);
      await loadTeams();
    } catch { return; } finally {
      setAddingMember(false);
    }
  }

  async function handleRemoveMember(userId: string) {
    if (!selectedTeam) return;
    await adminApi.removeTeamMember(selectedTeam.id, userId);
    await openTeam(selectedTeam.id);
    await loadTeams();
  }

  async function handleAddDashboard() {
    if (!selectedTeam || !addDashboardId) return;
    setAddingDashboard(true);
    try {
      await adminApi.addTeamDashboard(selectedTeam.id, Number(addDashboardId));
      setAddDashboardId("");
      await openTeam(selectedTeam.id);
    } catch { return; } finally {
      setAddingDashboard(false);
    }
  }

  async function handleRemoveDashboard(dashboardId: number) {
    if (!selectedTeam) return;
    await adminApi.removeTeamDashboard(selectedTeam.id, dashboardId);
    await openTeam(selectedTeam.id);
  }

  async function handleAddReport() {
    if (!selectedTeam || !addReportId) return;
    setAddingReport(true);
    try {
      await adminApi.addTeamReport(selectedTeam.id, addReportId);
      setAddReportId("");
      await openTeam(selectedTeam.id);
    } catch { return; } finally {
      setAddingReport(false);
    }
  }

  async function handleRemoveReport(reportId: string) {
    if (!selectedTeam) return;
    await adminApi.removeTeamReport(selectedTeam.id, reportId);
    await openTeam(selectedTeam.id);
  }

  // Users NOT already in the selected team, for the add member dropdown
  const addableUsers = users.filter(
    (u) => u.is_active && !selectedTeam?.members.some((m) => m.id === u.id)
  );

  // Dashboards NOT already in the team defaults
  const addableDashboards = availableDashboards.filter(
    (d) => !selectedTeam?.default_dashboards.some((dd) => dd.id === d.id)
  );

  const addableReports = availableReports.filter(
    (r) => !selectedTeam?.default_reports.some((dr) => dr.id === r.id)
  );

  if (selectedTeam) {
    return (
      <div>
        <button
          onClick={() => setSelectedTeam(null)}
          className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800"
        >
          <ChevronLeft size={15} /> All teams
        </button>

        {/* Team header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          {editing ? (
            <div className="flex-1 space-y-2">
              <input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold focus:border-blue-500 focus:outline-none"
                placeholder="Team name"
              />
              <input
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-500 focus:border-blue-500 focus:outline-none"
                placeholder="Description (optional)"
              />
              {editError && <p className="text-xs text-red-600">{editError}</p>}
              <div className="flex gap-2">
                <Button onClick={handleSaveEdit} disabled={editSaving}>
                  {editSaving ? "Saving…" : "Save"}
                </Button>
                <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div>
              <h2 className="text-base font-semibold text-gray-900">{selectedTeam.name}</h2>
              {selectedTeam.description && (
                <p className="mt-0.5 text-sm text-gray-500">{selectedTeam.description}</p>
              )}
            </div>
          )}
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="rounded p-1.5 text-gray-400 hover:text-blue-600"
              title="Edit team"
            >
              <Pencil size={14} />
            </button>
          )}
        </div>

        {detailLoading ? (
          <LoadingText />
        ) : (
          <div className="space-y-6">
            {/* Members */}
            <section>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700">
                  Members <span className="ml-1 text-gray-400 font-normal">({selectedTeam.members.length})</span>
                </h3>
              </div>

              {selectedTeam.members.length === 0 ? (
                <p className="text-sm text-gray-400 mb-3">No members yet.</p>
              ) : (
                <div className="mb-3 rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {selectedTeam.members.map((member) => (
                    <div key={member.id} className="flex items-center gap-3 px-4 py-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800">{member.full_name}</p>
                        <p className="text-xs text-gray-400">{member.email}</p>
                      </div>
                      <button
                        onClick={() => handleRemoveMember(member.id)}
                        className="rounded p-1 text-gray-400 hover:text-red-500"
                        title="Remove from team"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {addableUsers.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select
                    value={addMemberUserId}
                    onChange={(e) => setAddMemberUserId(e.target.value)}
                    className="flex-1"
                  >
                    <option value="">Add a member…</option>
                    {addableUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
                    ))}
                  </Select>
                  <Button onClick={handleAddMember} disabled={!addMemberUserId || addingMember}>
                    <UserPlus size={14} />
                    {addingMember ? "Adding…" : "Add"}
                  </Button>
                </div>
              )}
            </section>

            {/* Default dashboards */}
            <section>
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Default dashboards{" "}
                  <span className="ml-1 text-gray-400 font-normal">
                    ({selectedTeam.default_dashboards.length})
                  </span>
                </h3>
                <p className="mt-0.5 text-xs text-gray-400">
                  Shared read-only with new members when they join this team.
                </p>
              </div>

              {selectedTeam.default_dashboards.length > 0 ? (
                <div className="mb-3 rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {selectedTeam.default_dashboards.map((dash) => (
                    <div key={dash.id} className="flex items-center gap-3 px-4 py-2.5">
                      <LayoutDashboard size={14} className="shrink-0 text-gray-400" />
                      <span className="flex-1 text-sm text-gray-800">{dash.name}</span>
                      <button
                        onClick={() => handleRemoveDashboard(dash.id)}
                        className="rounded p-1 text-gray-400 hover:text-red-500"
                        title="Remove from defaults"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 mb-3">No default dashboards set.</p>
              )}

              {addableDashboards.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select
                    value={addDashboardId}
                    onChange={(e) => setAddDashboardId(e.target.value)}
                    className="flex-1"
                  >
                    <option value="">Add a dashboard…</option>
                    {addableDashboards.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </Select>
                  <Button onClick={handleAddDashboard} disabled={!addDashboardId || addingDashboard}>
                    <Plus size={14} />
                    {addingDashboard ? "Adding…" : "Add"}
                  </Button>
                </div>
              )}
            </section>

            {/* Default reports */}
            <section>
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Default reports{" "}
                  <span className="ml-1 text-gray-400 font-normal">
                    ({selectedTeam.default_reports.length})
                  </span>
                </h3>
                <p className="mt-0.5 text-xs text-gray-400">
                  Shared read-only with new members when they join this team.
                </p>
              </div>

              {selectedTeam.default_reports.length > 0 ? (
                <div className="mb-3 rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {selectedTeam.default_reports.map((report) => (
                    <div key={report.id} className="flex items-center gap-3 px-4 py-2.5">
                      <FileText size={14} className="shrink-0 text-gray-400" />
                      <span className="flex-1 text-sm text-gray-800">{report.name}</span>
                      <button
                        onClick={() => handleRemoveReport(report.id)}
                        className="rounded p-1 text-gray-400 hover:text-red-500"
                        title="Remove from defaults"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 mb-3">No default reports set.</p>
              )}

              {addableReports.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select
                    value={addReportId}
                    onChange={(e) => setAddReportId(e.target.value)}
                    className="flex-1"
                  >
                    <option value="">Add a report…</option>
                    {addableReports.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </Select>
                  <Button onClick={handleAddReport} disabled={!addReportId || addingReport}>
                    <Plus size={14} />
                    {addingReport ? "Adding…" : "Add"}
                  </Button>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Teams</h2>
          <p className="mt-1 text-sm text-gray-500">
            Group users into teams. New team members automatically receive the team's default dashboards and reports, shared read-only.
          </p>
        </div>
        <Button onClick={() => setShowCreate((v) => !v)}>
          <Plus size={14} /> New team
        </Button>
      </div>

      {showCreate && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Create team</p>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Name</label>
                <input
                  type="text" value={createName} onChange={(e) => setCreateName(e.target.value)}
                  placeholder="e.g. Product team"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700">Description <span className="text-gray-400">(optional)</span></label>
                <input
                  type="text" value={createDesc} onChange={(e) => setCreateDesc(e.target.value)}
                  placeholder="What this team does…"
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>
            {createError && <p className="text-xs text-red-600">{createError}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" type="button" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button type="submit" disabled={creating}>
                <Plus size={14} /> {creating ? "Creating…" : "Create team"}
              </Button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <LoadingText />
      ) : teams.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 px-5 py-8 text-center">
          <p className="text-sm text-gray-400">No teams yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
          {teams.map((team) => (
            <div
              key={team.id}
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
              onClick={() => openTeam(team.id)}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">{team.name}</p>
                {team.description && (
                  <p className="text-xs text-gray-400 truncate">{team.description}</p>
                )}
                <div className="mt-1 flex items-center gap-3 text-xs text-gray-400">
                  <span>{team.member_count} {team.member_count === 1 ? "member" : "members"}</span>
                  <span>·</span>
                  <span>{team.dashboard_count} default {team.dashboard_count === 1 ? "dashboard" : "dashboards"}</span>
                </div>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); handleDelete(team); }}
                className="rounded p-1.5 text-gray-400 hover:text-red-500"
                title="Delete team"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
